import { ESupportedGames } from "../../@types";
import { IAllstarUser } from "../../@types/user";
import { TPlayer } from "./@types";

const { REACT_APP_S3_CLIP_BUCKET } = process.env;

export const getMapKey = (hero: string | undefined): string | undefined => {
  if (!hero) return undefined;
  const filename = hero.split("/").pop()?.split(".")[0] || "";
  return filename.replace(/_\d+$/, "") || undefined;
};

export const getMapIconUrl = (hero: string | undefined): string | undefined => {
  const mapKey = getMapKey(hero);
  if (!mapKey) return undefined;
  return `${REACT_APP_S3_CLIP_BUCKET}/static/web/cs2_icons/map_icon/${mapKey}.png`;
};

const parseDate = (date: string) =>
  /^\d+$/.test(date) ? new Date(parseInt(date)) : new Date(date);

export const formatDateRelative = (date: string) => {
  const parsed = parseDate(date);
  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return parsed.toLocaleString("en-us", { month: "short", day: "numeric" });
};

export const formatDateFull = (date: string) => {
  const parsed = parseDate(date);
  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const sameYear = parsed.getFullYear() === now.getFullYear();

  if (diffMins < 60)
    return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24)
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  if (sameYear) {
    return parsed.toLocaleString("en-us", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return parsed.toLocaleString("en-us", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDate = (date: string) => {
  return parseDate(date).toLocaleString("en-us", {
    month: "short",
    day: "numeric",
  });
};
const WEAPON_SHORT_NAMES: Record<string, string> = {
  "Desert Eagle": "Deagle",
  "Dual Berettas": "Dualies",
  "Five-SeveN": "Five-7",
  "Sawed-Off": "Sawed-Off",
  "PP-Bizon": "Bizon",
};

export const shortenMomentTitle = (title: string) => {
  let result = title;
  for (const [full, short] of Object.entries(WEAPON_SHORT_NAMES)) {
    result = result.replace(full, short);
  }
  return result;
};

export const formatTime = (time: number) => {
  const date = new Date(time * 1000).toISOString();
  return time > 3600 ? date.substring(11, 19) : date.substring(14, 19);
};
export const formatGameMode = (gameMode: string) =>
  `${gameMode.charAt(0)}${gameMode.slice(1).toLowerCase()}`;
export const formatDuration = (seconds: number) =>
  `${~~(seconds / 60)}:${seconds % 60}`;
export const mustLinkAnAccount = ({
  game,
  allstarUser,
}: {
  game: ESupportedGames;
  allstarUser: IAllstarUser;
}) => {
  switch (game) {
    case ESupportedGames.VALORANT:
    case ESupportedGames.LOL:
      return !allstarUser?.user?.riot?.active;
    case ESupportedGames.DOTA2:
    case ESupportedGames.CS2:
      return (
        !allstarUser?.user?.steam?.active &&
        !allstarUser?.user?.faceit?.nickname
      );
    case ESupportedGames.FORTNITE:
      return !allstarUser?.user?.epic?.id;
    case ESupportedGames.OW2:
      return !allstarUser?.user?.battlenet?.battletag;
    case ESupportedGames.NONE:
      return true;

    default:
      return null;
  }
};
export const getPlayerSearchIdentifier = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("playerSearchIdentifier") || "";
};
export const getPlayerGameIdByGame = (
  game: ESupportedGames | undefined,
  user: IAllstarUser,
): string | null => {
  if (!game) return null;
  switch (game) {
    case ESupportedGames.DOTA2:
    case ESupportedGames.CS2:
      return user?.user?.steam?.steamid ?? null;
    case ESupportedGames.LOL:
      return user?.user?.riot?.puuid ?? null;
    case ESupportedGames.FORTNITE:
      return user?.user?.epic?.id ?? null;
    case ESupportedGames.OW2:
      return user?.user?.battlenet?.battletag ?? null;
    case ESupportedGames.VALORANT:
      return user?.user?.riot?.riotId ?? null;
    default:
      return null;
  }
};

export const getMatchHistoryGameEnum = (game?: string | undefined) => {
  if (!game) return ESupportedGames.NONE;
  switch (game) {
    case "COUNTER_STRIKE_2":
    case "CS2":
      return ESupportedGames.CS2;
    case "COUNTER_STRIKE_GO":
      return ESupportedGames.CSGO;
    case "FORTNITE":
      return ESupportedGames.FORTNITE;
    case "DOTA2":
      return ESupportedGames.DOTA2;
    case "LEAGUE_OF_LEGENDS":
    case "LOL":
      return ESupportedGames.LOL;
    case "OVERWATCH_2":
    case "OW2":
      return ESupportedGames.OW2;
  }
};

export const getTextColorByMatchResult = (result: string) => {
  switch (result) {
    case "Win":
      return "envy";
    case "Victory":
      return "envy";
    case "Tie":
      return "error";
    case "Loss":
      return "alert";
    case "Defeat":
      return "alert";
  }
};
